/**
 * Canonical single source of truth for trail surfaces.
 *
 * These values are duplicated across several elevation-profile renderers
 * (the route planner's `createChartData` getter, PlanTrailElevation.vue, the
 * hike map profile, …). This module unifies them so every surface, colour and
 * human label is defined once.
 *
 * IMPORTANT: the integer keys MUST match the GraphQL wire contract
 * (`surfaces: [Integer]`) and the values shipped in already-deployed clients.
 * They are copied verbatim from the previously-duplicated maps — do not change
 * them without a wire-compat review.
 */

/**
 * Integer → surface-key map. Mirrors GraphQL `surfaces: [Integer]` and
 * `LineStringPropertiesSurface.surfaceIntToString`.
 */
export const surfaceIntToKey: Record<number, string> = {
  0: 'unknown',
  1: 'paved',
  2: 'unpaved',
  3: 'asphalt',
  4: 'concrete',
  5: 'cobblestone',
  6: 'metal',
  7: 'wood',
  8: 'compactedGravel',
  9: 'fineGravel',
  10: 'gravel',
  11: 'dirt',
  12: 'ground',
  13: 'ice',
  14: 'pavingStones',
  15: 'sand',
  16: 'woodchips',
  17: 'grass',
  18: 'grassPaver',
};

/**
 * Surface-key → integer map (inverse of {@link surfaceIntToKey}). Includes the
 * `missing` alias, which maps to `unknown` (0), matching the planner.
 */
export const surfaceKeyToInt: Record<string, number> = {
  unknown: 0,
  paved: 1,
  unpaved: 2,
  asphalt: 3,
  concrete: 4,
  cobblestone: 5,
  metal: 6,
  wood: 7,
  compactedGravel: 8,
  fineGravel: 9,
  gravel: 10,
  dirt: 11,
  ground: 12,
  ice: 13,
  pavingStones: 14,
  sand: 15,
  woodchips: 16,
  grass: 17,
  grassPaver: 18,
  missing: 0, // treat missing as unknown
};

/**
 * Surface-key → line colour. Vibrant colours for the segment-coloured line.
 */
export const surfaceColors: Record<string, string> = {
  unknown: '#CCCCCC', // light gray
  missing: '#CCCCCC', // light gray
  paved: '#888888', // medium gray
  unpaved: '#CD853F', // peru
  asphalt: '#666666', // dark gray
  concrete: '#BEBEBE', // light gray
  cobblestone: '#D2691E', // chocolate
  metal: '#B0C4DE', // light steel blue
  wood: '#DEB887', // burlywood
  compactedGravel: '#A9A9A9', // dark gray
  fineGravel: '#C0C0C0', // silver
  gravel: '#A9A9A9', // dark gray
  dirt: '#8B4513', // saddle brown
  ground: '#D2691E', // chocolate
  ice: '#87CEEB', // sky blue
  pavingStones: '#999999', // medium gray
  sand: '#F4A460', // sandy brown
  woodchips: '#DEB887', // burlywood
  grass: '#32CD32', // lime green
  grassPaver: '#9ACD32', // yellow green
};

/**
 * Surface-key → human-readable label.
 */
export const surfaceNames: Record<string, string> = {
  unknown: 'Unknown',
  missing: 'Unknown',
  paved: 'Paved',
  unpaved: 'Unpaved',
  asphalt: 'Asphalt',
  concrete: 'Concrete',
  cobblestone: 'Cobblestone',
  metal: 'Metal',
  wood: 'Wood',
  compactedGravel: 'Compacted Gravel',
  fineGravel: 'Fine Gravel',
  gravel: 'Gravel',
  dirt: 'Dirt',
  ground: 'Ground',
  ice: 'Ice',
  pavingStones: 'Paving Stones',
  sand: 'Sand',
  woodchips: 'Woodchips',
  grass: 'Grass',
  grassPaver: 'Grass Paver',
};

/** Fallback colour when a surface is missing/unrecognised. */
export const DEFAULT_SURFACE_COLOR = '#CCCCCC';

export interface SurfaceInfo {
  /** Canonical surface key, e.g. `'gravel'`. */
  key: string;
  /** Human-readable label, e.g. `'Gravel'`. */
  name: string;
  /** Line colour hex, e.g. `'#A9A9A9'`. */
  color: string;
}

/**
 * Resolve a surface integer (as delivered on the wire) to its key, human label
 * and colour. Unknown/undefined values resolve to the `unknown` surface.
 */
export function surfaceForValue(value: number | null | undefined): SurfaceInfo {
  const key =
    value != null && surfaceIntToKey[value] != null
      ? surfaceIntToKey[value]
      : 'unknown';
  return {
    key,
    name: surfaceNames[key] ?? surfaceNames.unknown,
    color: surfaceColors[key] ?? DEFAULT_SURFACE_COLOR,
  };
}

/**
 * Resolve a surface key (or `missing`) to its colour. Convenience for the
 * Chart.js `segment.borderColor` callback which works in surface keys.
 */
export function surfaceColorForKey(key: string | null | undefined): string {
  if (!key) return DEFAULT_SURFACE_COLOR;
  return surfaceColors[key] ?? DEFAULT_SURFACE_COLOR;
}
